
import { mapState } from "vuex";
import { Component, Vue } from "vue-property-decorator";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import WellnessSidebar from "@/components/wellness/WellnessSidebar.vue";
import AllergensEmptyState from "@/components/allergens/AllergensEmptyState.vue";
import AllergenAnalysisResults from "@/components/allergens/AllergenAnalysisResults.vue";
import vAuditLogModal from "@/components/vAuditLogModal.vue";
import vModal from "@/components/vModal.vue";
import type { WellnessItem } from "@/models/wellness/WellnessItem";
import type { WellnessTab } from "@/models/wellness/WellnessTab";
import { LocalizationState } from "@/store/modules/LocalizationModule";
import { AccountState } from "@/store/modules/AccountModule";
import { ItemCompact } from "@/models/ItemCompact";
import { AddOnType } from "@/models/Subscriptions/AddOns/AddOnType";
import { listRecipesThatUseIngredient } from "@/services/wellnessApi";
import AllergenMLRepository from "@/utils/API/AllergenMLRepository";
import { CountryAllergenSymptomsItem, MlAnalysisResponse, MlIngredientAnalysisResult } from "@/models/AllergenML/AllergenML";
import AllergenRepository from "@/utils/API/AllergenRepository";
import { AllergenDefinition } from "@/models/Allergen/AllergenDefinition";
import { AllergenConfigurationTargetType } from "@/models/Allergen/AllergenConfigurationData";
import { NutrientSource } from "@/models/NutrientSource/NutrientSource";
import { formatCountrySpecificAllergenName } from "@/utils/AllergenDisplayNames";
import { NutrientSourceList } from "@/models/NutrientSource/NutrientSourceList";
import RecipesRepository from "@/utils/API/RecipesRepository";
import SystemConfigurationRepository from "@/utils/API/SystemConfigurationRepository";
import AllergensConfiguration from "@/components/labelConfiguration/AllergensConfiguration.vue";

type MatrixColumn = {
  key: string;
  allergenCode: string;
  label: string;
  displayLabel?: string;
  iconClass: string;
  colorClass: string;
  isOptionalAllergen?: boolean;
  isFirstOptionalAllergen?: boolean;
  optionalGroupKey?: string;
};

type MatrixCellState = {
  status: "ai" | "user-added" | "user-confirmed" | "user-removed";
  iconClass: string;
  accentIconClass?: string;
  badgeClass: string;
  tooltip: string;
  isPresent: boolean;
};

type MatrixRow = {
  key: string;
  ingredientId?: number | null;
  ingredient: string;
  flagged: string[];
  overviewFlagged: string[];
  flaggedAllergenCodes: Record<string, string[]>;
  symptomNotesByKey: Record<string, string>;
  cellStates: Record<string, MatrixCellState>;
};

const matrixColumns: MatrixColumn[] = [
  { key: "celery", allergenCode: "CELERY", label: "Celery", iconClass: "allergen-symbol allergen-symbol-celery", colorClass: "celery" },
  { key: "gluten", allergenCode: "GLUTEN", label: "Cereals containing Gluten", displayLabel: "Gluten", iconClass: "allergen-symbol allergen-symbol-wheat", colorClass: "gluten" },
  { key: "crustaceans", allergenCode: "CRUSTACEANS", label: "Crustaceans", iconClass: "allergen-symbol allergen-symbol-crustaceans", colorClass: "crustaceans" },
  { key: "egg", allergenCode: "EGG", label: "Eggs", iconClass: "allergen-symbol allergen-symbol-egg", colorClass: "egg" },
  { key: "fish", allergenCode: "FISH", label: "Fish", iconClass: "allergen-symbol allergen-symbol-fish", colorClass: "fish" },
  { key: "lupin", allergenCode: "LUPIN", label: "Lupin", iconClass: "allergen-symbol allergen-symbol-lupin", colorClass: "lupin" },
  { key: "milk", allergenCode: "MILK", label: "Milk", iconClass: "allergen-symbol allergen-symbol-milk", colorClass: "milk" },
  { key: "molluscs", allergenCode: "MOLLUSCS", label: "Molluscs", iconClass: "allergen-symbol allergen-symbol-molluscs", colorClass: "molluscs" },
  { key: "mustard", allergenCode: "MUSTARD", label: "Mustard", iconClass: "allergen-symbol allergen-symbol-mustard", colorClass: "mustard" },
  { key: "treeNuts", allergenCode: "TREE_NUTS", label: "Nuts/Tree Nuts", displayLabel: "Tree nuts", iconClass: "allergen-symbol allergen-symbol-nuts", colorClass: "treeNuts" },
  { key: "peanuts", allergenCode: "PEANUT", label: "Peanuts", iconClass: "allergen-symbol allergen-symbol-peanut", colorClass: "peanuts" },
  { key: "sesame", allergenCode: "SESAME_SEED", label: "Sesame Seeds", displayLabel: "Sesame", iconClass: "allergen-symbol allergen-symbol-sesame", colorClass: "sesame" },
  { key: "soy", allergenCode: "SOY", label: "Soy/Soybean", displayLabel: "Soya", iconClass: "allergen-symbol allergen-symbol-soy", colorClass: "soy" },
  { key: "sulphites", allergenCode: "SULPHITES", label: "Sulphites", iconClass: "allergen-symbol allergen-symbol-sulphites", colorClass: "sulphites" },
  { key: "wheat", allergenCode: "WHEAT", label: "Wheat/Triticale", displayLabel: "Wheat", iconClass: "allergen-symbol allergen-symbol-wheat", colorClass: "wheat" },
];

  const menuSanoLogo = require("@/assets/img/logo-apple.png");

function normalizeAllergenKey(value: string) {
  const normalized = (value || "").toLowerCase().replace(/[^a-z]/g, "");

  switch (normalized) {
  case "celery":
    return "celery";
  case "milk":
  case "dairy":
    return "milk";
  case "egg":
  case "eggs":
    return "egg";
  case "fish":
    return "fish";
  case "crustaceans":
  case "crustacean":
    return "crustaceans";
  case "molluscs":
  case "mollusks":
    return "molluscs";
  case "shellfish":
  case "crustaceansandmolluscs":
  case "crustaceansandmollusks":
    return "crustaceans";
  case "lupin":
    return "lupin";
  case "treenut":
  case "treenuts":
  case "nuts":
  case "treenutsnuts":
  case "nutstreenuts":
    return "treeNuts";
  case "peanut":
  case "peanuts":
    return "peanuts";
  case "gluten":
  case "cerealscontaininggluten":
  case "cerealsthatcontaingluten":
    return "gluten";
  case "wheat":
  case "triticale":
  case "wheatandtriticale":
  case "wheattriticale":
    return "wheat";
  case "soy":
  case "soya":
  case "soybean":
  case "soybeans":
  case "soysoybean":
  case "soysoyasoybeans":
  case "soyasoybeans":
  case "soysoya":
    return "soy";
  case "sesame":
  case "sesameseed":
  case "sesameseeds":
    return "sesame";
  case "mustard":
    return "mustard";
  case "sulphites":
  case "sulfites":
  case "sulphurdioxideandsulphites":
  case "sulfurdioxideandsulfites":
    return "sulphites";
  default:
    return "";
  }
}

function formatAllergenName(value: string) {
  return (value || "").split("/")[0].trim();
}

function isCerealsContainingGlutenName(value: string) {
  const normalized = (value || "").toLowerCase().replace(/[^a-z]/g, "");
  return normalized === "gluten"
    || normalized === "wheat"
    || normalized === "cerealscontaininggluten"
    || normalized === "wheatandtriticale"
    || normalized === "wheattriticale";
}

function resolvePresentationAllergenKey(allergenId: string, displayName: string) {
  return normalizeAllergenKey(displayName || "")
    || normalizeAllergenKey(allergenId || "");
}

type SharedPropagationReviewState = {
  show: boolean,
  actionTitle: string,
  disclaimer: string,
  isLoading: boolean,
  pendingCount: number,
};

function createDefaultSharedPropagationReviewState(): SharedPropagationReviewState {
  return {
    show: false,
    actionTitle: "Apply Changes To Recipe",
    disclaimer: "",
    isLoading: false,
    pendingCount: 0,
  };
}

@Component({
  components: {
    vBreadcrumbs,
    WellnessSidebar,
    AllergensEmptyState,
    AllergenAnalysisResults,
    AllergensConfiguration,
    vAuditLogModal,
    vModal,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class AllergensAi extends Vue {
  readonly supportedJurisdictionSources = [
    NutrientSource.Canada,
    NutrientSource.US,
    NutrientSource.UK,
  ];
  localizationState!: LocalizationState;
  accountState!: AccountState;
  refreshKey = 0;
  selectedItem: WellnessItem | null = null;
  routeSelectedItemId: number | null = null;
  loadRouteSelectedAnalysis = false;
  selectedTab: WellnessTab = "recipes";
  loading = false;
  loadingMode: "saved" | "analyze" | null = null;
  exportingPdf = false;
  errorMessage = "";
  analysisResult: MlAnalysisResponse | null = null;
  analysisProgress = 0;
  progressTimer: number | null = null;
  selectedCountry: NutrientSource = NutrientSource.Canada;
  analysisRunId = 0;
  countryLoadId = 0;
  showMatrixScrollLeft = false;
  showMatrixScrollRight = false;
  showOverrideScrollLeft = false;
  showOverrideScrollRight = false;
  showOverrideScrollUp = false;
  showOverrideScrollDown = false;
  showOverrideGrid = false;
  handleWindowResize = () => this.updateAllergenScrollCues();
  optionalBadgeRefreshKey = 0;
  showAuditLogModal = false;
  auditModalTitle = "";
  auditLogItem: ItemCompact = {
    id: 0,
    name: "",
    createdOn: null,
    updatedOn: null,
    createdUserName: "",
    updatedUserName: "",
  };
  ingredientUsageModal = {
    isOpen: false,
    recipes: [] as Array<{ id: number; name: string }>,
  };
  overrideFloatingTooltip = {
    visible: false,
    content: "",
    placement: "top" as "top" | "bottom",
    left: 0,
    top: 0,
    width: 720,
    minWidth: 320,
  };
  nutrientSourceSettings: NutrientSourceList[] = [];
  loadingJurisdictions = false;
  countryOptions: Array<{ label: string; value: NutrientSource }> = [];
  countryAllergenSymptoms: CountryAllergenSymptomsItem[] = [];
  visibleAllergenCatalog: AllergenDefinition[] = [];
  countryAllergenCatalogLoadId = 0;
  matrixColumns = matrixColumns;
  ingredientOrderKeys: string[] = [];
  savingOverrideKey = "";
  overrideConfirmation: {
    isOpen: boolean;
    row: MatrixRow | null;
    column: MatrixColumn | null;
    isAllergen: boolean;
    allergenCode: string;
    ingredientName: string;
    allergenLabel: string;
    wasAiDetected: boolean;
  } = {
    isOpen: false,
    row: null,
    column: null,
    isAllergen: false,
    allergenCode: "",
    ingredientName: "",
    allergenLabel: "",
    wasAiDetected: false,
  };
  sharedAllergenSelections: any[] = [];
  sharedMayContainText: Record<string, string[]> = {};
  sharedPropagationReview: SharedPropagationReviewState = createDefaultSharedPropagationReviewState();
  sharedAllergensConfigurationProxyKey = 0;
  applyingRecipeChangesFromAi = false;

  get translations() {
    return this.localizationState.translations;
  }

  t(key: string, fallback: string, ...args: Array<string | number>) {
    const translation = this.translations?.get(key, ...args);
    return translation && translation !== "---" ? translation : fallback;
  }

  get isAllergenAiAddonActive() {
    const addOns = this.accountState?.userDetails?.addOns || this.$store.getters.getUserDetails?.addOns || [];
    return addOns.some((addOn: any) => {
      const addOnType = typeof addOn === "object" ? addOn.addOn : addOn;
      return addOn?.isActive !== false && (addOnType === AddOnType.AllergenAI || addOnType === "AllergenAI");
    });
  }

  get restaurantId() {
    return this.accountState?.userDetails?.restaurantId || 0;
  }

  get canAnalyze() {
    return !!this.selectedItem && this.restaurantId > 0;
  }

  get canExportAnalysis() {
    return !!this.selectedItem && !!this.analysisResult && !this.loading && !this.exportingPdf;
  }

  get showPreAnalysisCard() {
    return !!this.selectedItem && (!this.analysisResult || this.loading) && !this.errorMessage;
  }

  get totalItemsScanned() {
    return this.analysisResult?.ingredients.length || 0;
  }

  get itemsWithAllergens() {
    return this.matrixRows.filter((row) => row.flagged.length > 0).length;
  }

  get selectedTypeLabel() {
    if (this.selectedTab === "recipes") {
      return this.t("allergens_ai_item_type_recipe", "Recipe");
    }

    if (this.selectedTab === "combinedRecipes") {
      return this.t("allergens_ai_item_type_combined_recipe", "Combined Recipe");
    }

    return this.t("allergens_ai_item_type_custom_ingredient", "Custom Ingredient");
  }

  get selectedTypeLabelLowerCase() {
    return this.selectedTypeLabel.toLowerCase();
  }

  get sharedPropagationActionTitle() {
    if (this.selectedTargetType === "ingredient") {
      return "Apply Changes To Custom Ingredients";
    }

    if (this.selectedTargetType === "combined-recipe") {
      return "Apply Changes To Combined Recipe";
    }

    return "Apply Changes To Recipe";
  }

  get selectedItemDisplayName() {
    if (!this.selectedItem) {
      return "";
    }

    return this.selectedItem.name || this.t("allergens_ai_untitled", "Untitled");
  }

  get selectedTargetType() {
    if (this.selectedTab === "recipes") {
      return "recipe";
    }

    if (this.selectedTab === "combinedRecipes") {
      return "combined-recipe";
    }

    return "ingredient";
  }

  get selectedCountryLabel() {
    return this.countryOptions.find((option) => option.value === this.selectedCountry)?.label || "Canada";
  }

  get isCustomIngredientTab() {
    return this.selectedTab === "customRecipes";
  }

  get showCountrySelection() {
    return !this.isCustomIngredientTab;
  }

  get selectedScopeLabel() {
    return this.isCustomIngredientTab
      ? this.t("allergens_ai_scope_all_allergens", "All Allergens")
      : this.selectedCountryLabel;
  }

  get noAllergenDetectedCopy() {
    if (this.isCustomIngredientTab) {
      return this.t(
        "allergens_ai_no_allergen_detected_copy_custom_ingredient",
        "No allergens were detected in this custom ingredient."
      );
    }

    return this.t(
      "allergens_ai_no_allergen_detected_copy_by_country",
      `No allergens were detected in ${this.selectedCountryLabel}`,
      this.selectedCountryLabel
    );
  }

  get noContainsStatementCopy() {
    if (this.isCustomIngredientTab) {
      return this.t(
        "allergens_ai_no_contains_statement_custom_ingredient",
        "No contains statement was generated for this custom ingredient."
      );
    }

    return this.t(
      "allergens_ai_no_contains_statement_by_country",
      `No contains statements generated in ${this.selectedCountryLabel}`,
      this.selectedCountryLabel
    );
  }

  get mascotName() {
    return this.t("allergens_ai_mascot_name", "MenuSano AI");
  }

  get mascotKicker() {
    return this.t("allergens_ai_mascot_name", "MenuSano AI");
  }

  get mascotStatusLabel() {
    if (this.loading) {
      return this.loadingMode === "saved"
        ? this.t("allergens_ai_status_opening_saved_results", "Opening saved results...")
        : this.t("allergens_ai_status_analyzing", "Analyzing...");
    }

    if (this.analysisResult) {
      return this.t("allergens_ai_status_results_ready", "Results ready");
    }

    if (this.selectedItem?.hasAllergenAnalysis) {
      return this.t("allergens_ai_status_saved_results_available", "Saved results available");
    }

    return this.t("allergens_ai_status_ready", "Ready to analyze");
  }

  get mascotStatusCopy() {
    if (this.isCustomIngredientTab) {
      if (this.loading) {
        return this.loadingMode === "saved"
          ? this.t(
            "allergens_ai_status_copy_opening_saved_custom",
            `${this.mascotName} is opening the saved allergen results for this custom ingredient.`,
            this.mascotName
          )
          : this.t(
            "allergens_ai_status_copy_analyzing_custom",
            `${this.mascotName} is checking this custom ingredient for any allergen signals.`,
            this.mascotName
          );
      }

      if (this.analysisResult) {
        return this.showNoAllergenState
          ? this.t(
            "allergens_ai_status_copy_complete_none_custom",
            `${this.mascotName} finished the review. No allergens were detected for this custom ingredient.`,
            this.mascotName
          )
          : this.t(
            "allergens_ai_status_copy_complete_results_custom",
            `${this.mascotName} finished the review. Check the detected allergens and manual review details below.`,
            this.mascotName
          );
      }

      return this.t(
        "allergens_ai_status_copy_ready_custom",
        `${this.mascotName} is ready to scan this custom ingredient for any allergen signals.`,
        this.mascotName
      );
    }

    if (this.loading) {
      return this.loadingMode === "saved"
        ? this.t(
          "allergens_ai_status_copy_opening_saved",
          `${this.mascotName} is opening the saved allergen results and preparing the ${this.selectedCountryLabel} guidance.`,
          this.mascotName,
          this.selectedCountryLabel
        )
        : this.t(
          "allergens_ai_status_copy_analyzing",
          `${this.mascotName} is checking ingredients, allergen signals, and ${this.selectedCountryLabel} label rules.`,
          this.mascotName,
          this.selectedCountryLabel
        );
    }

    if (this.analysisResult) {
      return this.showNoAllergenState
        ? this.t(
          "allergens_ai_status_copy_complete_none",
          `${this.mascotName} finished the review. No reportable allergens were detected for ${this.selectedCountryLabel}.`,
          this.mascotName,
          this.selectedCountryLabel
        )
        : this.t(
          "allergens_ai_status_copy_complete_results",
          `${this.mascotName} finished the review. Check the detected allergens and ${this.selectedCountryLabel} compliance details below.`,
          this.mascotName,
          this.selectedCountryLabel
        );
    }

    return this.t(
      "allergens_ai_status_copy_ready",
      `${this.mascotName} is ready to scan this item for allergens and ${this.selectedCountryLabel} compliance.`,
      this.mascotName,
      this.selectedCountryLabel
    );
  }

  get primaryActionLabel() {
    if (this.selectedItem?.hasAllergenAnalysis && !this.analysisResult) {
      return this.t("allergens_ai_primary_action_open_saved", "Open Saved Results");
    }

    return this.t("allergens_ai_primary_action_analyze", "Analyze Allergens");
  }

  get selectedSourceNavigationLabel() {
    return this.t("allergens_ai_open_source", "Open");
  }

  get loadingStatusLabel() {
    return this.loadingMode === "saved"
      ? this.t("allergens_ai_loading_status_opening_saved", "Opening Saved Results")
      : this.t("allergens_ai_loading_status_running", "Analyze Running");
  }

  get loadingStageLabel() {
    if (this.isCustomIngredientTab) {
      if (this.loadingMode === "saved") {
        if (this.analysisProgress < 40) {
          return this.t("allergens_ai_loading_stage_opening_saved", "Opening saved allergen results");
        }

        if (this.analysisProgress < 75) {
          return this.t(
            "allergens_ai_loading_stage_loading_saved_custom",
            "Loading saved allergen guidance for this custom ingredient"
          );
        }

        return this.t("allergens_ai_loading_stage_preparing_saved_results", "Preparing saved results for review");
      }

      if (this.analysisProgress < 35) {
        return this.t(
          "allergens_ai_loading_stage_scanning_ingredients",
          `Scanning ${this.selectedTypeLabel.toLowerCase()} ingredients`,
          this.selectedTypeLabel.toLowerCase()
        );
      }

      if (this.analysisProgress < 72) {
        return this.t(
          "allergens_ai_loading_stage_reviewing_evidence_custom",
          "Reviewing allergen evidence"
        );
      }

      return this.t(
        "allergens_ai_loading_stage_preparing_summary_custom",
        "Preparing allergen summary"
      );
    }

    if (this.loadingMode === "saved") {
      if (this.analysisProgress < 40) {
        return this.t("allergens_ai_loading_stage_opening_saved", "Opening saved allergen results");
      }

      if (this.analysisProgress < 75) {
        return this.t(
          "allergens_ai_loading_stage_loading_saved_guidance",
          `Loading saved guidance for ${this.selectedCountryLabel}`,
          this.selectedCountryLabel
        );
      }

      return this.t("allergens_ai_loading_stage_preparing_saved_results", "Preparing saved results for review");
    }

    if (this.analysisProgress < 35) {
      return this.t(
        "allergens_ai_loading_stage_scanning_ingredients",
        `Scanning ${this.selectedTypeLabel.toLowerCase()} ingredients`,
        this.selectedTypeLabel.toLowerCase()
      );
    }

    if (this.analysisProgress < 72) {
      return this.t(
        "allergens_ai_loading_stage_reviewing_evidence",
        `Reviewing evidence for ${this.selectedCountryLabel}`,
        this.selectedCountryLabel
      );
    }

    return this.t(
      "allergens_ai_loading_stage_preparing_compliance",
      `Preparing ${this.selectedCountryLabel} compliance output`,
      this.selectedCountryLabel
    );
  }

  get loadingStageCopy() {
    if (this.isCustomIngredientTab && this.loadingMode === "saved" && this.analysisProgress >= 40 && this.analysisProgress < 75) {
      return this.t(
        "allergens_ai_loading_copy_loading_saved_custom",
        "Loading detected allergens, overrides, and saved guidance for this custom ingredient."
      );
    }

    if (this.loadingMode === "saved") {
      if (this.analysisProgress < 40) {
        return this.t("allergens_ai_loading_copy_pull_saved", "Pulling the saved allergen review for the selected item.");
      }

      if (this.analysisProgress < 75) {
        return this.t("allergens_ai_loading_copy_loading_saved", "Loading detected allergens, overrides, and country-specific guidance from the saved result.");
      }

      return this.t("allergens_ai_loading_copy_finishing_saved", "Finishing the saved view so everything is ready to review.");
    }

    if (this.analysisProgress < 35) {
      return this.t("allergens_ai_loading_copy_tracing", "Tracing the selected recipe and pulling the ingredient list into the analysis matrix.");
    }

    if (this.analysisProgress < 72) {
      return this.t("allergens_ai_loading_copy_checking_signals", "Checking saved allergen signals, ingredient evidence, and any prior overrides tied to this selection.");
    }

    return this.t("allergens_ai_loading_copy_finishing_preview", "Finishing the declaration preview so the result is ready to review as soon as the run completes.");
  }

  get analysisGeneratedLabel() {
    if (!this.analysisResult?.generatedOnUtc) {
      return "";
    }

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(this.analysisResult.generatedOnUtc));
  }

  get workspaceDescription() {
    if (this.selectedItem && !this.analysisResult && !this.loading && !this.errorMessage) {
      if (this.selectedItem.hasAllergenAnalysis) {
        return this.t(
          "allergens_ai_workspace_saved_results_available_report_by_type",
          `Saved allergen results are available for this ${this.selectedTypeLabelLowerCase}. Open the saved report to view or edit the ${this.selectedTypeLabelLowerCase} allergen report.`,
          this.selectedTypeLabelLowerCase
        );
      }

      return this.t("allergens_ai_workspace_quick_scan", "Let MenuSano AI quickly scan your ingredients and help you identify possible allergens with confidence.");
    }

    if (this.selectedTab === "customRecipes") {
      return this.t(
        "allergens_ai_workspace_custom_ingredient",
        "Review allergen signals for this custom ingredient and confirm any allergen detections before you use the result."
      );
    }

    if (this.selectedTab === "combinedRecipes") {
      return this.t(
        "allergens_ai_workspace_combined_recipe",
        `Review inherited allergen signals, confirm the ingredient matrix, and validate the ${this.selectedCountryLabel} label output.`,
        this.selectedCountryLabel
      );
    }

    return this.t(
      "allergens_ai_workspace_recipe",
      "Review detected allergens, make any needed changes, and update the recipe switches.",
      this.selectedCountryLabel
    );
  }

  get matrixRows(): MatrixRow[] {
    if (!this.analysisResult) {
      return [];
    }

    const orderedIngredients = this.analysisResult.ingredients
      .map((ingredient, index) => ({ ingredient, index }))
      .sort((left, right) => {
        const leftOrder = this.ingredientOrderIndex(left.ingredient);
        const rightOrder = this.ingredientOrderIndex(right.ingredient);

        return leftOrder === rightOrder
          ? left.index - right.index
          : leftOrder - rightOrder;
      })
      .map((item) => item.ingredient);

    const rows = orderedIngredients.map((ingredient) => {
      const flaggedAllergenCodes: Record<string, string[]> = {};
      const overviewFlaggedAllergenCodes: Record<string, string[]> = {};
      const symptomNotesByKey: Record<string, string> = {};
      const cellStates: Record<string, MatrixCellState> = {};

      ingredient.allergens.forEach((allergen) => {
        const normalizedKey = this.normalizeCountryPresentationAllergenKey(resolvePresentationAllergenKey(
          allergen.id || "",
          allergen.displayName || ""
        ));

        if (!normalizedKey) {
          return;
        }

        const allergenCode = allergen.id || allergen.displayName || normalizedKey;
        if (!flaggedAllergenCodes[normalizedKey]) {
          flaggedAllergenCodes[normalizedKey] = [];
        }

        if (flaggedAllergenCodes[normalizedKey].indexOf(allergenCode) === -1) {
          flaggedAllergenCodes[normalizedKey].push(allergenCode);
        }

        const notes = (allergen.symptomNotes || [])
          .map((note) => this.stripSymptomHtml(note || ""))
          .filter((note) => note !== "");
        if (notes.length > 0 && !symptomNotesByKey[normalizedKey]) {
          symptomNotesByKey[normalizedKey] = notes.join(" ");
        }

        const shouldShowInOverview = allergen.isJurisdictionReportable !== false && this.isReportableAllergenKey(normalizedKey)
          || this.isOptionalVisibleAllergenKey(normalizedKey);

        if (shouldShowInOverview) {
          if (!overviewFlaggedAllergenCodes[normalizedKey]) {
            overviewFlaggedAllergenCodes[normalizedKey] = [];
          }

          if (overviewFlaggedAllergenCodes[normalizedKey].indexOf(allergenCode) === -1) {
            overviewFlaggedAllergenCodes[normalizedKey].push(allergenCode);
          }

          const isUserOverride = this.isUserOverridePrediction(allergen);
          cellStates[normalizedKey] = this.buildMatrixCellState(
            normalizedKey,
            allergen.displayName || allergen.id || normalizedKey,
            isUserOverride ? "user-added" : "ai"
          );
        }
      });

      (ingredient.overrideMarkers || []).forEach((marker) => {
        const normalizedKey = this.normalizeCountryPresentationAllergenKey(resolvePresentationAllergenKey(
          marker.allergenCode || "",
          marker.displayName || ""
        ));

        if (marker.isJurisdictionReportable === false && !this.isOptionalVisibleAllergenKey(normalizedKey)) {
          return;
        }

        if (!normalizedKey) {
          return;
        }

        const allergenCode = marker.allergenCode || marker.displayName || normalizedKey;
        if (!flaggedAllergenCodes[normalizedKey]) {
          flaggedAllergenCodes[normalizedKey] = [];
        }

        if (flaggedAllergenCodes[normalizedKey].indexOf(allergenCode) === -1) {
          flaggedAllergenCodes[normalizedKey].push(allergenCode);
        }

        if (this.isReportableAllergenKey(normalizedKey) || this.isOptionalVisibleAllergenKey(normalizedKey)) {
          if (!overviewFlaggedAllergenCodes[normalizedKey]) {
            overviewFlaggedAllergenCodes[normalizedKey] = [];
          }

          if (overviewFlaggedAllergenCodes[normalizedKey].indexOf(allergenCode) === -1) {
            overviewFlaggedAllergenCodes[normalizedKey].push(allergenCode);
          }
        }

        if (!marker.isAllergen) {
          flaggedAllergenCodes[normalizedKey] = flaggedAllergenCodes[normalizedKey]
            .filter((code) => code !== allergenCode);
        }

        cellStates[normalizedKey] = this.buildMatrixCellState(
          normalizedKey,
          marker.displayName || marker.allergenCode || normalizedKey,
          marker.isAllergen
            ? marker.wasAiDetected ? "ai" : "user-added"
            : "user-removed"
        );
      });

      return {
        key: this.ingredientMatrixKey(ingredient),
        ingredientId: ingredient.ingredientId,
        ingredient: ingredient.ingredientName,
        flagged: Object.keys(flaggedAllergenCodes).filter((key) => flaggedAllergenCodes[key].length > 0),
        overviewFlagged: Object.keys(overviewFlaggedAllergenCodes)
          .filter((key) => overviewFlaggedAllergenCodes[key].length > 0),
        flaggedAllergenCodes,
        symptomNotesByKey,
        cellStates,
      };
    });

    return this.mergeDuplicateMatrixRows(rows);
  }

  get overviewMatrixColumns(): MatrixColumn[] {
    const presentKeys = new Set<string>();

    this.matrixRows.forEach((row) => {
      row.overviewFlagged.forEach((key) => presentKeys.add(key));
    });

    return this.matrixColumns
      .filter((column) => presentKeys.has(column.key))
      .map((column) => this.resolveCountryMatrixColumn(column))
      .sort(this.sortOptionalColumnsToEnd)
      .map((column, index, columns) => this.markFirstOptionalColumn(column, index, columns));
  }

  get overrideMatrixColumns(): MatrixColumn[] {
    return this.matrixColumns
      .filter((column) => this.isReportableOverrideColumn(column) || this.isOptionalVisibleAllergenKey(column.key))
      .map((column) => this.resolveCountryMatrixColumn(column))
      .sort(this.sortOptionalColumnsToEnd)
      .map((column, index, columns) => this.markFirstOptionalColumn(column, index, columns));
  }

  get hasOverviewOptionalAllergens() {
    return this.overviewMatrixColumns.some((column) => column.isOptionalAllergen);
  }

  get hasOverrideOptionalAllergens() {
    return this.overrideMatrixColumns.some((column) => column.isOptionalAllergen);
  }

  get countryReportableAllergenKeys() {
    if (this.isCustomIngredientTab) {
      return new Set<string>();
    }

    return new Set<string>(
      this.countryAllergenSymptoms
        .map((allergen) => this.normalizeCountryPresentationAllergenKey(resolvePresentationAllergenKey(
          allergen.allergenCode || "",
          allergen.displayName || ""
        )))
        .filter((key) => key !== "")
    );
  }

  get visibleAllergenKeys() {
    return new Set<string>(
      this.visibleAllergenCatalog
        .map((allergen) => this.normalizeCountryPresentationAllergenKey(resolvePresentationAllergenKey(
          allergen.code || "",
          allergen.name || ""
        )))
        .filter((key) => key !== "")
    );
  }

  get countryOptionalOverrideKeys() {
    if (this.isCustomIngredientTab) {
      return new Set<string>();
    }

    if (this.selectedCountry === NutrientSource.US) {
      return new Set<string>(["molluscs", "gluten", "mustard", "sulphites", "celery", "lupin"]);
    }

    if (this.selectedCountry === NutrientSource.Canada) {
      return new Set<string>(["gluten", "celery", "lupin"]);
    }

    return new Set<string>();
  }

  isReportableAllergenKey(allergenKey: string) {
    const reportableKeys = this.countryReportableAllergenKeys;
    const optionalOverrideKeys = this.countryOptionalOverrideKeys;

    if (optionalOverrideKeys.has(allergenKey)) {
      return false;
    }

    return reportableKeys.size === 0 || reportableKeys.has(allergenKey);
  }

  isOptionalVisibleAllergenKey(allergenKey: string) {
    const reportableKeys = this.countryReportableAllergenKeys;
    const optionalOverrideKeys = this.countryOptionalOverrideKeys;

    if (optionalOverrideKeys.has(allergenKey)) {
      return this.visibleAllergenKeys.has(allergenKey);
    }

    return reportableKeys.size > 0
      && this.visibleAllergenKeys.has(allergenKey)
      && !reportableKeys.has(allergenKey);
  }

  isReportableOverrideColumn(column: MatrixColumn) {
    return this.isReportableAllergenKey(column.key);
  }

  sortOptionalColumnsToEnd(left: MatrixColumn, right: MatrixColumn) {
    if (left.isOptionalAllergen !== right.isOptionalAllergen) {
      return left.isOptionalAllergen ? 1 : -1;
    }

    if (!left.isOptionalAllergen && !right.isOptionalAllergen) {
      return 0;
    }

    const optionalGroupOrderDifference = this.getOptionalGroupOrder(left) - this.getOptionalGroupOrder(right);

    if (optionalGroupOrderDifference !== 0) {
      return optionalGroupOrderDifference;
    }

    return 0;
  }

  markFirstOptionalColumn(column: MatrixColumn, index: number, columns: MatrixColumn[]) {
    return {
      ...column,
      isFirstOptionalAllergen: Boolean(
        column.isOptionalAllergen
        && (
          !columns[index - 1]?.isOptionalAllergen
          || columns[index - 1]?.optionalGroupKey !== column.optionalGroupKey
        )
      ),
    };
  }

  countOptionalColumns(columns: MatrixColumn[]) {
    return columns.filter((column) => column.isOptionalAllergen).length;
  }

  countCountryRuleColumns(columns: MatrixColumn[]) {
    return columns.filter((column) => !column.isOptionalAllergen).length;
  }

  getOptionalFloatingBadgeStyle(columns: MatrixColumn[], shellRefName: string, scrollerRefName: string) {
    void this.optionalBadgeRefreshKey;

    const measuredStyle = this.getMeasuredOptionalFloatingBadgeStyle(shellRefName, scrollerRefName);
    if (measuredStyle) {
      return measuredStyle;
    }

    const ingredientColumnWidth = 280;
    const allergenColumnWidth = 92;
    const optionalColumnCount = this.countOptionalColumns(columns);
    const countryColumnCount = this.countCountryRuleColumns(columns);

    return {
      left: `${ingredientColumnWidth + (countryColumnCount * allergenColumnWidth) + ((optionalColumnCount * allergenColumnWidth) / 2)}px`,
    };
  }

  getMeasuredOptionalFloatingBadgeStyle(shellRefName: string, scrollerRefName: string) {
    const shell = this.getElementRef(shellRefName);
    const scroller = this.getElementRef(scrollerRefName);

    if (!shell || !scroller) {
      return null;
    }

    const optionalHeadings = Array.from(scroller.querySelectorAll<HTMLElement>(".allergens-column-heading-optional"));

    if (optionalHeadings.length === 0) {
      return null;
    }

    const shellRect = shell.getBoundingClientRect();
    const firstOptionalRect = optionalHeadings[0].getBoundingClientRect();
    const lastOptionalRect = optionalHeadings[optionalHeadings.length - 1].getBoundingClientRect();
    const optionalCenter = ((firstOptionalRect.left + lastOptionalRect.right) / 2) - shellRect.left;

    return {
      left: `${optionalCenter}px`,
    };
  }

  getElementRef(refName: string): HTMLElement | null {
    const ref = this.$refs[refName];
    const element = Array.isArray(ref) ? ref[0] : ref;

    return element instanceof HTMLElement ? element : null;
  }

  resolveCountryMatrixColumn(column: MatrixColumn): MatrixColumn {
    if (this.isCustomIngredientTab) {
      return {
        ...column,
        label: formatCountrySpecificAllergenName(column.label),
        displayLabel: column.displayLabel
          ? formatCountrySpecificAllergenName(column.displayLabel)
          : column.displayLabel,
        isOptionalAllergen: false,
        optionalGroupKey: this.getOptionalGroupKey(column.key),
      };
    }

    if (this.selectedCountry === NutrientSource.UK && column.key === "gluten") {
      return {
        ...column,
        label: "Cereals containing Gluten",
        displayLabel: "Cereals containing Gluten",
        optionalGroupKey: this.getOptionalGroupKey(column.key),
      };
    }

    return {
      ...column,
      label: formatCountrySpecificAllergenName(column.label, this.selectedCountry),
      displayLabel: column.displayLabel
        ? formatCountrySpecificAllergenName(column.displayLabel, this.selectedCountry)
        : column.displayLabel,
      isOptionalAllergen: this.isOptionalVisibleAllergenKey(column.key),
      optionalGroupKey: this.getOptionalGroupKey(column.key),
    };
  }

  getOptionalGroupKey(allergenKey: string) {
    if (this.isCustomIngredientTab) {
      return `optional-${allergenKey}`;
    }

    if (this.selectedCountry === NutrientSource.US) {
      if (allergenKey === "molluscs") {
        return "optional-us-mollusks";
      }

      if (["gluten", "mustard", "sulphites"].includes(allergenKey)) {
        return "optional-us-gluten-mustard-sulfites";
      }

      if (["celery", "lupin"].includes(allergenKey)) {
        return "optional-us-celery-lupin";
      }
    }

    if (this.selectedCountry === NutrientSource.Canada) {
      if (allergenKey === "gluten") {
        return "optional-ca-gluten";
      }

      if (["celery", "lupin"].includes(allergenKey)) {
        return "optional-ca-celery-lupin";
      }
    }

    return `optional-${allergenKey}`;
  }

  getOptionalGroupOrder(column: MatrixColumn) {
    if (!column.isOptionalAllergen) {
      return -1;
    }

    if (this.isCustomIngredientTab) {
      return 99;
    }

    const optionalGroupKey = column.optionalGroupKey || this.getOptionalGroupKey(column.key);

    if (this.selectedCountry === NutrientSource.US) {
      if (optionalGroupKey === "optional-us-mollusks") {
        return 0;
      }

      if (optionalGroupKey === "optional-us-gluten-mustard-sulfites") {
        return 1;
      }

      if (optionalGroupKey === "optional-us-celery-lupin") {
        return 2;
      }
    }

    if (this.selectedCountry === NutrientSource.Canada) {
      if (optionalGroupKey === "optional-ca-gluten") {
        return 0;
      }

      if (optionalGroupKey === "optional-ca-celery-lupin") {
        return 1;
      }
    }

    return 99;
  }

  normalizeCountryPresentationAllergenKey(allergenKey: string) {
    if (this.isCustomIngredientTab) {
      return allergenKey;
    }

    if (this.selectedCountry === NutrientSource.UK && allergenKey === "wheat") {
      return "gluten";
    }

    return allergenKey;
  }

  get declaredAllergenText() {
    const allergens = this.analysisResult?.labelPresentation?.declaredAllergens || [];
    return allergens.length > 0
      ? allergens.map((allergen) => this.formatAllergenName(allergen)).join(", ")
        : this.t("allergens_ai_no_declared_allergens", "No declared allergens generated.");
  }

  get declaredAllergenList() {
    return this.analysisResult?.labelPresentation?.declaredAllergens || [];
  }

  get detectedAllergenKeys() {
    const detectedKeys = new Set<string>();

    this.matrixRows.forEach((row) => {
      row.flagged.forEach((key) => {
        if (this.isReportableAllergenKey(key) || this.isOptionalVisibleAllergenKey(key)) {
          detectedKeys.add(key);
        }
      });
    });

    return Array.from(detectedKeys);
  }

  get detectedAllergenCount() {
    return this.detectedAllergenKeys.length;
  }

  get overrideSummaryEntries() {
    const entries: Array<{
      key: string;
      ingredient: string;
      allergenKey: string;
      allergenLabel: string;
      actionLabel: string;
    }> = [];

    this.matrixRows.forEach((row) => {
      Object.entries(row.cellStates).forEach(([allergenKey, state]) => {
        if (state.status === "ai") {
          return;
        }

        const column = this.matrixColumns.find((item) => item.key === allergenKey);
        const allergenLabel = this.formatAllergenName(column?.displayLabel || column?.label || allergenKey);
        const actionLabel = state.status === "user-removed"
          ? this.t("allergens_ai_override_action_removed", "overridden as not an allergen")
          : this.t("allergens_ai_override_action_added", "overridden as an allergen");

        entries.push({
          key: `${row.key}-${allergenKey}-${state.status}`,
          ingredient: row.ingredient,
          allergenKey,
          allergenLabel,
          actionLabel,
        });
      });
    });

    return entries;
  }

  get overrideFloatingTooltipStyle() {
    const style: Record<string, string> = {
      left: `${this.overrideFloatingTooltip.left}px`,
      top: `${this.overrideFloatingTooltip.top}px`,
      maxWidth: `min(${this.overrideFloatingTooltip.width}px, calc(100vw - 32px))`,
    };

    if (this.overrideFloatingTooltip.minWidth > 0) {
      style.minWidth = `min(${this.overrideFloatingTooltip.minWidth}px, calc(100vw - 32px))`;
    }

    return style;
  }

  get warningEntries() {
    return (this.analysisResult?.warnings || []).filter(
      (warning) => !this.isAdminProcessingWarning(warning)
    );
  }

  get analysisWarnings() {
    return this.warningEntries.length
      ? this.warningEntries
      : [this.t("allergens_ai_warning_none", "No warnings for this selection.")];
  }

  get showNoAllergenState() {
    return !!this.analysisResult && (this.analysisResult.allergens || []).length === 0;
  }

  get overrideConfirmationModalTitle() {
    return this.overrideConfirmation.wasAiDetected
      ? this.t("allergens_ai_override_modal_review", "Review Allergen Override")
      : this.t("allergens_ai_override_modal_confirm", "Confirm Allergen Override");
  }

  get overrideConfirmationIntro() {
    return this.overrideConfirmation.wasAiDetected
      ? this.t("allergens_ai_override_intro_review", "You are about to override an AI-detected allergen for ")
      : this.t("allergens_ai_override_intro_confirm", "Please confirm you want to override the AI detection for ");
  }

  get overrideConfirmationQuestion() {
    return this.overrideConfirmation.wasAiDetected
      ? this.t("allergens_ai_override_question_review", "This manual decision will replace the AI result and will be used for allergen declarations and final label guidance.")
      : this.t("allergens_ai_override_question_confirm", "This manual decision will be used for allergen declarations and final label guidance.");
  }


  created() {
    this.initializeSelectionFromRoute();

    this.countryOptions = this.buildFallbackCountryOptions();

    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["common", "recipes", "dishes", "ingredients"],
    }).catch((error: any) => {
      if (error.response) {
        this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
      }
      else {
        this.$snotify.error(this.t("network-error", "Network error"));
      }
    });
  }

  async mounted() {
    window.addEventListener("resize", this.handleWindowResize);
    await this.fetchJurisdictionSources();
    await this.loadCountryAllergenSymptoms();
    await this.loadSelectedRouteAnalysisIfNeeded();
  }

  beforeDestroy() {
    this.hideAllergenPopovers();
    this.hideOverrideFloatingTooltip();
    window.removeEventListener("resize", this.handleWindowResize);
    this.stopProgressSimulation();
  }

  resetAnalysis() {
    this.hideAllergenPopovers();
    this.hideOverrideFloatingTooltip();
    this.analysisRunId += 1;
    this.stopProgressSimulation();
    this.loading = false;
    this.loadingMode = null;
    this.errorMessage = "";
    this.analysisResult = null;
    this.ingredientOrderKeys = [];
    this.analysisProgress = 0;
    this.showOverrideGrid = false;
    this.showMatrixScrollLeft = false;
    this.showMatrixScrollRight = false;
    this.showOverrideScrollLeft = false;
    this.showOverrideScrollRight = false;
    this.showOverrideScrollUp = false;
    this.showOverrideScrollDown = false;
    this.sharedPropagationReview = createDefaultSharedPropagationReviewState();
  }

  async loadCountryAllergenSymptoms() {
    const loadId = ++this.countryAllergenCatalogLoadId;

    try {
      const visibleAllergensResponse = await AllergenRepository.listAllergens();

      if (this.isCustomIngredientTab) {
        if (loadId !== this.countryAllergenCatalogLoadId) {
          return;
        }

        this.countryAllergenSymptoms = [];
        this.visibleAllergenCatalog = visibleAllergensResponse.data || [];
        return;
      }

      const response = await AllergenMLRepository.getCountryAllergenSymptoms(
        this.selectedCountry,
        this.selectedCountry
      );

      if (loadId !== this.countryAllergenCatalogLoadId) {
        return;
      }

      this.countryAllergenSymptoms = response.data || [];
      this.visibleAllergenCatalog = visibleAllergensResponse.data || [];
    }
    catch {
      if (loadId === this.countryAllergenCatalogLoadId) {
        this.countryAllergenSymptoms = [];
        this.visibleAllergenCatalog = [];
      }
    }
  }

  buildFallbackCountryOptions() {
    return this.supportedJurisdictionSources.map((source) => ({
      label: this.nutrientSourceLabel(source),
      value: source,
    }));
  }

  nutrientSourceLabel(value: NutrientSource) {
    switch (value) {
    case NutrientSource.US:
      return "USA";
    case NutrientSource.UK:
      return "UK";
    case NutrientSource.Canada:
    default:
      return "Canada";
    }
  }

  fetchJurisdictionSources() {
    this.loadingJurisdictions = true;

    return SystemConfigurationRepository.fetchNutrientSourcesList()
      .then((response) => {
        const sourceSettings = response.data?.result || [];
        this.nutrientSourceSettings = sourceSettings;

        const enabledJurisdictions = sourceSettings
          .filter((source: NutrientSourceList) =>
            source.isEnabled && this.supportedJurisdictionSources.includes(source.nutrientSource))
          .map((source: NutrientSourceList) => ({
            label: this.nutrientSourceLabel(source.nutrientSource),
            value: source.nutrientSource,
          }));

        this.countryOptions = enabledJurisdictions.length > 0
          ? enabledJurisdictions
          : this.buildFallbackCountryOptions();

        if (!this.countryOptions.find((option) => option.value === this.selectedCountry)) {
          this.selectedCountry = this.countryOptions[0]?.value ?? NutrientSource.Canada;
        }

        return this.loadCountryAllergenSymptoms();
      })
      .catch(() => {
        this.countryOptions = this.buildFallbackCountryOptions();
        return this.loadCountryAllergenSymptoms();
      })
      .finally(() => {
        this.loadingJurisdictions = false;
      });
  }

  async handleSelect(payload: { item: WellnessItem; tab: WellnessTab }) {
    this.selectedTab = payload.tab;
    this.selectedItem = { ...payload.item };
    this.routeSelectedItemId = Number(payload.item.id);
    this.loadRouteSelectedAnalysis = false;
    this.selectedCountry = NutrientSource.Canada;
    this.resetAnalysis();
    await this.syncSelectedCountry(Number(payload.item.id), payload.tab);
    await this.loadSharedAllergenConfiguration();
  }

  async handleTabChange(tab: WellnessTab) {
    this.selectedTab = tab;
    this.selectedItem = null;
    this.routeSelectedItemId = null;
    this.selectedCountry = NutrientSource.Canada;
    this.resetAnalysis();
    await this.loadCountryAllergenSymptoms();
    await this.loadSharedAllergenConfiguration();
  }

  initializeSelectionFromRoute() {
    const queryTab = this.normalizeRouteTab(this.$route.query.tab);
    const queryId = Number(this.$route.query.id);
    const queryName = this.normalizeRouteString(this.$route.query.name);

    if (queryTab) {
      this.selectedTab = queryTab;
    }

    if (Number.isFinite(queryId) && queryId > 0) {
      this.routeSelectedItemId = queryId;
      this.loadRouteSelectedAnalysis = this.$route.query.analyzed === "1";

      if (queryName) {
        this.selectedItem = {
          id: queryId,
          name: queryName,
        };
      }
    }
  }

  normalizeRouteTab(tab: unknown): WellnessTab | null {
    if (tab === "recipes" || tab === "combinedRecipes" || tab === "customRecipes") {
      return tab;
    }

    return null;
  }

  normalizeRouteString(value: unknown) {
    return typeof value === "string" ? value : "";
  }

  async loadSelectedRouteAnalysisIfNeeded() {
    if (!this.selectedItem) {
      await this.loadSharedAllergenConfiguration();
      return;
    }

    await this.syncSelectedCountry(Number(this.selectedItem.id), this.selectedTab);
    await this.loadSharedAllergenConfiguration();

    if (this.loadRouteSelectedAnalysis) {
      await this.startAnalysis();
    }
  }

  async syncSelectedCountry(itemId: number, tab: WellnessTab) {
    const loadId = ++this.countryLoadId;

    if (tab === "customRecipes") {
      this.selectedCountry = this.countryOptions[0]?.value ?? NutrientSource.Canada;
      await this.loadCountryAllergenSymptoms();
      return;
    }

    try {
      const metadataResponse = await RecipesRepository.getRecipeMetaData(itemId);
      if (loadId !== this.countryLoadId || !this.selectedItem || Number(this.selectedItem.id) !== itemId) {
        return;
      }

      let countryId = Number(metadataResponse.data?.countryId);
      if (!this.supportedJurisdictionSources.includes(countryId)) {
        const detailsResponse = await RecipesRepository.getRecipeDetails(itemId, true);
        if (loadId !== this.countryLoadId || !this.selectedItem || Number(this.selectedItem.id) !== itemId) {
          return;
        }

        countryId = Number(detailsResponse.data?.nutritionCountry?.id);
      }

      const preferredCountry = this.supportedJurisdictionSources.includes(countryId)
        ? countryId as NutrientSource
        : NutrientSource.Canada;
      this.selectedCountry = this.countryOptions.find((option) => option.value === preferredCountry)?.value
        ?? this.countryOptions[0]?.value
        ?? NutrientSource.Canada;
      await this.loadCountryAllergenSymptoms();
    }
    catch {
      if (loadId === this.countryLoadId) {
        this.selectedCountry = this.countryOptions[0]?.value ?? NutrientSource.Canada;
        await this.loadCountryAllergenSymptoms();
      }
    }
  }

  async handleCountryChange(event: Event) {
    const target = event.target as HTMLSelectElement | null;
    this.selectedCountry = Number(target?.value ?? NutrientSource.Canada) as NutrientSource;
    this.resetAnalysis();
    await this.loadCountryAllergenSymptoms();
    await this.loadSharedAllergenConfiguration();
  }

  openSelectedSourceItem() {
    if (!this.selectedItem) {
      return;
    }

    const itemId = String(this.selectedItem.id);

    if (this.selectedTab === "recipes") {
      this.$router.push({ name: "recipesEdit", params: { id: itemId } });
      return;
    }

    if (this.selectedTab === "combinedRecipes") {
      this.$router.push({ name: "dishesEdit", params: { id: itemId } });
      return;
    }

    this.$router.push({ name: "ingredients" });
  }

  async startAnalysis() {
    if (!this.selectedItem || !this.canAnalyze) {
      return;
    }

    const runId = ++this.analysisRunId;
    const isOpeningSavedResults = this.selectedItem.hasAllergenAnalysis && !this.analysisResult;

    this.loading = true;
    this.loadingMode = isOpeningSavedResults ? "saved" : "analyze";
    this.errorMessage = "";
    this.analysisResult = null;
    this.ingredientOrderKeys = [];
    this.analysisProgress = 0;
    this.startProgressSimulation();

    try {
      await new Promise<void>((resolve) => this.$nextTick(resolve));

      const responsePromise = this.selectedTargetType === "ingredient"
        ? AllergenMLRepository.getSavedIngredientAnalysis({
          restaurantId: this.restaurantId,
          ingredientId: Number(this.selectedItem.id),
          countryId: this.showCountrySelection ? this.selectedCountry : undefined,
          nutrientSource: this.showCountrySelection ? this.selectedCountry : undefined,
        })
        : AllergenMLRepository.getSavedRecipeAnalysis({
          restaurantId: this.restaurantId,
          recipeId: Number(this.selectedItem.id),
          isCombinedRecipe: this.selectedTargetType === "combined-recipe",
          countryId: this.selectedCountry,
          nutrientSource: this.selectedCountry,
        });

      const response = await responsePromise;

      if (runId !== this.analysisRunId || !this.selectedItem) {
        return;
        }

        await this.completeProgressSimulation(runId);
        this.applyAnalysisResult(response.data, false);
        await this.handleAnalysisAutoApply(response.data, isOpeningSavedResults);
        this.$nextTick(() => this.updateAllergenScrollCues());
      }
    catch (error: any) {
      if (runId !== this.analysisRunId) {
        return;
      }

      await this.completeProgressSimulation(runId);
      this.errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errorMessages?.[0]?.userVisibleMessage ||
        error?.message ||
        this.t("allergens_ai_analysis_failed", "The Allergen AI analysis could not be completed.");
    }
    finally {
      if (runId === this.analysisRunId) {
        this.loading = false;
        this.loadingMode = null;
      }
    }
  }

  openAllergenOverrideConfirmation(row: MatrixRow, column: MatrixColumn) {
    if (!this.selectedItem || !this.analysisResult || !row.ingredientId || this.savingOverrideKey) {
      return;
    }

    if (!this.isReportableOverrideColumn(column) && !column.isOptionalAllergen) {
      this.$snotify.error(
        this.t(
          "allergens_ai_not_reportable_for_country",
          `${column.label} is not reportable for ${this.selectedCountryLabel}.`,
          column.label,
          this.selectedCountryLabel
        )
      );
      return;
    }

    const wasFlagged = row.flagged.indexOf(column.key) !== -1;
    const isAllergen = !wasFlagged;
    const allergenCode = this.resolveOverrideAllergenCode(row, column, isAllergen);
    const currentState = row.cellStates[column.key];
    const wasAiDetected = currentState?.status === "ai";

    this.overrideConfirmation = {
      isOpen: true,
      row,
      column,
      isAllergen,
      allergenCode,
      ingredientName: row.ingredient,
      allergenLabel: column.label,
      wasAiDetected,
    };
  }

  closeAllergenOverrideConfirmation() {
    if (this.savingOverrideKey) {
      return;
    }

    this.resetAllergenOverrideConfirmation();
  }

  resetAllergenOverrideConfirmation() {
    this.overrideConfirmation = {
      isOpen: false,
      row: null,
      column: null,
      isAllergen: false,
      allergenCode: "",
      ingredientName: "",
      allergenLabel: "",
      wasAiDetected: false,
    };
  }

  async confirmAllergenOverride() {
    const { row, column, isAllergen, allergenCode } = this.overrideConfirmation;

    if (!row || !column) {
      this.closeAllergenOverrideConfirmation();
      return;
    }

    if (!this.isReportableOverrideColumn(column) && !column.isOptionalAllergen) {
      this.$snotify.error(
        this.t(
          "allergens_ai_not_reportable_for_country",
          `${column.label} is not reportable for ${this.selectedCountryLabel}.`,
          column.label,
          this.selectedCountryLabel
        )
      );
      this.resetAllergenOverrideConfirmation();
      return;
    }

    await this.toggleAllergenOverride(row, column, isAllergen, allergenCode);
  }

  async toggleAllergenOverride(row: MatrixRow, column: MatrixColumn, isAllergen: boolean, allergenCode: string) {
    if (!this.selectedItem || !this.analysisResult || !row.ingredientId || this.savingOverrideKey) {
      return;
    }

    const overrideKey = this.overrideKey(row, column);
    this.savingOverrideKey = overrideKey;

    try {
      const response = this.selectedTargetType === "ingredient"
        ? await AllergenMLRepository.overrideIngredientAllergen({
          restaurantId: this.restaurantId,
          ingredientId: row.ingredientId,
          countryId: this.selectedCountry,
          nutrientSource: this.selectedCountry,
          allergenCode,
          isAllergen,
          reason: isAllergen
            ? this.t(
              "allergens_ai_override_reason_add_column",
              `Marked ${column.label} as an allergen from AI Allergen Detector.`,
              column.label
            )
            : this.t(
              "allergens_ai_override_reason_remove_column",
              `Disabled ${column.label} as an allergen from AI Allergen Detector.`,
              column.label
            ),
        })
        : await AllergenMLRepository.overrideRecipeIngredientAllergen({
          restaurantId: this.restaurantId,
          recipeId: Number(this.selectedItem.id),
          ingredientId: row.ingredientId,
          countryId: this.selectedCountry,
          nutrientSource: this.selectedCountry,
          allergenCode,
          isAllergen,
          reason: isAllergen
            ? this.t(
              "allergens_ai_override_reason_add_ingredient",
              `Marked ${row.ingredient} as ${column.label} from AI Allergen Detector.`,
              row.ingredient,
              column.label
            )
            : this.t(
              "allergens_ai_override_reason_remove_ingredient",
              `Disabled ${column.label} for ${row.ingredient} from AI Allergen Detector.`,
              column.label,
              row.ingredient
            ),
        });

      this.applyAnalysisResult(response.data, true);
      this.$snotify.success(
        isAllergen
          ? this.t("allergens_ai_override_applied", "Allergen override applied.")
          : this.t("allergens_ai_override_disabled", "Allergen disabled for this item.")
      );
      this.resetAllergenOverrideConfirmation();
      this.$nextTick(() => this.updateAllergenScrollCues());
    }
    catch (error: any) {
      this.$snotify.error(
        error?.response?.data?.message ||
        error?.response?.data?.errorMessages?.[0]?.userVisibleMessage ||
        error?.message ||
        this.t("allergens_ai_override_save_failed", "The allergen override could not be saved.")
      );
    }
    finally {
      if (this.savingOverrideKey === overrideKey) {
        this.savingOverrideKey = "";
      }
    }
  }

  resolveOverrideAllergenCode(row: MatrixRow, column: MatrixColumn, isAllergen: boolean) {
    if (isAllergen) {
      return column.allergenCode;
    }

    return row.flaggedAllergenCodes[column.key]?.[0] || column.allergenCode;
  }

  overrideKey(row: MatrixRow, column: MatrixColumn) {
    return `${row.key}-${column.key}`;
  }

  isOverrideSaving(row: MatrixRow, column: MatrixColumn) {
    return this.savingOverrideKey === this.overrideKey(row, column);
  }

    get overviewUsageTooltip() {
      return this.t("allergens_ai_overview_tooltip", "Hover over an allergen name to review common reaction symptoms.");
    }

    get allergenSymptomNotesLookup() {
      const lookup: Record<string, string> = {};

      this.countryAllergenSymptoms.forEach((allergen) => {
        const key = resolvePresentationAllergenKey(
          allergen.allergenCode || "",
          allergen.displayName || ""
        );
        const notes = (allergen.symptomNotes || [])
          .map((note) => this.stripSymptomHtml(note || ""))
          .filter((note) => note !== "");

        if (key && notes.length > 0) {
          lookup[key] = notes.join(" ");
        }
      });

      return lookup;
    }

  stripSymptomHtml(value: string) {
    const withoutTags = (value || "").replace(/<[^>]+>/g, " ").trim();
    const textarea = document.createElement("textarea");
    textarea.innerHTML = withoutTags;
    return textarea.value.replace(/\s+/g, " ").trim();
  }

  get overviewLegendTooltipHtml() {
    return [
      "<div class='allergens-popover-legend'>",
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge ai'><i class='fa fa-check'></i></span><span>${this.t("allergens_ai_legend_ai", "AI detected this allergen.")}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge removed'><i class='fa fa-times'></i></span><span>${this.t("allergens_ai_legend_removed", "Manually marked as not an allergen.")}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge user'><i class='fa fa-check'></i><span class='allergens-popover-badge-accent'><i class='fa fa-question'></i></span></span><span>${this.t("allergens_ai_legend_user_added", "Manually added as an allergen.")}</span></div>`,
      "</div>",
    ].join("");
  }

  hideAllergenPopovers() {
    const root = (this as any).$root;
    if (root?.$emit) {
      root.$emit("bv::hide::popover");
      root.$emit("bv::hide::tooltip");
    }
  }

  showOverrideFloatingTooltip(
    event: FocusEvent | MouseEvent,
    content: string,
    _isHtml = false,
    placement: "top" | "bottom" = "top",
    width = 720,
    minWidth = 320
  ) {
    const target = event.currentTarget as HTMLElement | null;
    if (!target || !content) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const maxWidth = width;
    const viewportPadding = 16;
    const rawLeft = rect.left + (rect.width / 2);
    const left = Math.min(
      Math.max(rawLeft, (maxWidth / 2) + viewportPadding),
      window.innerWidth - (maxWidth / 2) - viewportPadding
    );
    const top = placement === "bottom"
      ? rect.bottom + 14
      : rect.top - 14;

    this.overrideFloatingTooltip = {
      visible: true,
      content,
      placement,
      left,
      top,
      width,
      minWidth,
    };
  }

  hideOverrideFloatingTooltip() {
    this.overrideFloatingTooltip.visible = false;
  }

  isUserOverridePrediction(allergen: { source?: string | null; primaryEvidenceType?: string | null; evidence?: string[] | null }) {
    const source = (allergen.source || "").toLowerCase();
    const evidenceType = (allergen.primaryEvidenceType || "").toLowerCase();
    const evidenceText = (allergen.evidence || []).join(" ").toLowerCase();

    return source.indexOf("override") !== -1
      || evidenceType === "override"
      || evidenceText.indexOf("manual") !== -1;
  }

  async loadSharedAllergenConfiguration() {
    if (!this.selectedItem) {
      this.sharedAllergenSelections = [];
      this.sharedMayContainText = {};
      return true;
    }

    try {
      const response = await AllergenRepository.getStoredAllergenConfiguration(
        this.selectedTargetType === "ingredient"
          ? AllergenConfigurationTargetType.Ingredient
          : AllergenConfigurationTargetType.Recipe,
        Number(this.selectedItem.id)
      );

      this.sharedAllergenSelections = response.data?.allergens || [];
      this.sharedMayContainText = response.data?.mayContainText || {};
      this.sharedAllergensConfigurationProxyKey += 1;
      return true;
    }
    catch (error: any) {
      this.$snotify.error(
        error?.response?.data?.message
        || error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
        || error?.message
        || this.t("allergens_ai_shared_config_load_failed", "Could not load the current allergen configuration. Please try again.")
      );
      return false;
    }
  }

  async handleAnalysisAutoApply(
    _result: MlAnalysisResponse,
    isOpeningSavedResults: boolean,
    reloadSharedConfiguration = true
  ) {
    if (!this.isAllergenAiAddonActive || isOpeningSavedResults || !this.selectedItem) {
      return;
    }

    if (reloadSharedConfiguration) {
      const loadedSharedConfiguration = await this.loadSharedAllergenConfiguration();

      if (!loadedSharedConfiguration) {
        return;
      }
    }
    await this.$nextTick();

    const sharedAllergensConfigurationProxy = this.$refs.sharedAllergensConfigurationProxy as
      | {
        applyAiAnalysis?: (result: MlAnalysisResponse) => void,
        ensureAllergenOptionsReady?: () => Promise<void>
      }
      | undefined;

    await sharedAllergensConfigurationProxy?.ensureAllergenOptionsReady?.();
    sharedAllergensConfigurationProxy?.applyAiAnalysis?.(_result);
    await this.syncSharedPropagationReviewState();
  }

  onSharedAiAnalysisApplied() {
    this.$snotify.success(`AI allergens were applied in the selected ${this.selectedTypeLabelLowerCase}.`);
  }

  async applyCurrentOverrideResults() {
    if (!this.selectedItem || this.savingOverrideKey !== "" || this.applyingRecipeChangesFromAi || this.sharedPropagationReview.isLoading) {
      return;
    }

    const previousSharedPropagationReview = { ...this.sharedPropagationReview };
    this.sharedPropagationReview = {
      show: true,
      actionTitle: this.sharedPropagationActionTitle,
      disclaimer: this.sharedPropagationReview.disclaimer,
      isLoading: true,
      pendingCount: this.sharedPropagationReview.pendingCount,
    };
    this.applyingRecipeChangesFromAi = true;

    try {
      const response = this.selectedTargetType === "ingredient"
        ? await AllergenMLRepository.getSavedIngredientAnalysis({
          restaurantId: this.restaurantId,
          ingredientId: Number(this.selectedItem.id),
          countryId: this.showCountrySelection ? this.selectedCountry : undefined,
          nutrientSource: this.showCountrySelection ? this.selectedCountry : undefined,
        })
        : await AllergenMLRepository.getSavedRecipeAnalysis({
          restaurantId: this.restaurantId,
          recipeId: Number(this.selectedItem.id),
          isCombinedRecipe: this.selectedTargetType === "combined-recipe",
          countryId: this.selectedCountry,
          nutrientSource: this.selectedCountry,
        });

      this.updateIngredientOrder(response.data.ingredients, true);
      this.analysisResult = response.data;
      this.syncSelectedItemAllergenStatus(response.data);
      await this.handleAnalysisAutoApply(response.data, false, false);
    }
    catch (error: any) {
      this.sharedPropagationReview = previousSharedPropagationReview;
      this.$snotify.error(
        error?.response?.data?.message
        || error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
        || error?.message
        || this.t("allergens_ai_apply_override_failed", "Could not apply AI allergen changes. Please try again.")
      );
    }
    finally {
      this.applyingRecipeChangesFromAi = false;
    }
  }

  openSharedPropagationReviewModal() {
    const sharedAllergensConfigurationProxy = this.$refs.sharedAllergensConfigurationProxy as
      | { openPropagationReviewModal?: () => void }
      | undefined;

    sharedAllergensConfigurationProxy?.openPropagationReviewModal?.();
  }

  async syncSharedPropagationReviewState() {
    const sharedAllergensConfigurationProxy = this.$refs.sharedAllergensConfigurationProxy as
      | {
        getPropagationReviewState?: () => {
          show: boolean,
          actionTitle: string,
          disclaimer: string,
          isLoading: boolean,
          pendingCount: number,
        }
      }
      | undefined;

    if (!sharedAllergensConfigurationProxy?.getPropagationReviewState) {
      return;
    }

    const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
    const timeoutAt = Date.now() + 8000;
    const initialUiState = { ...this.sharedPropagationReview };
    let latestState = sharedAllergensConfigurationProxy.getPropagationReviewState();
    let sawPropagationActivity = latestState.isLoading || latestState.show || latestState.pendingCount > 0;

    if (sawPropagationActivity || !initialUiState.isLoading) {
      this.sharedPropagationReview = latestState;
    }

    while (Date.now() < timeoutAt) {
      await wait(100);
      latestState = sharedAllergensConfigurationProxy.getPropagationReviewState();

      if (latestState.isLoading || latestState.show || latestState.pendingCount > 0) {
        sawPropagationActivity = true;
        this.sharedPropagationReview = latestState;
      }

      if (sawPropagationActivity && !latestState.isLoading) {
        break;
      }
    }

    if (!sawPropagationActivity && !initialUiState.isLoading) {
      this.sharedPropagationReview = latestState;
    }
  }

  buildMatrixCellState(key: string, label: string, status: MatrixCellState["status"]): MatrixCellState {
    const fallbackAllergenLabel = this.t("allergens_ai_this_allergen", "this allergen");
    const displayLabel = formatCountrySpecificAllergenName(formatAllergenName(
      label || this.matrixColumns.find((column) => column.key === key)?.label || fallbackAllergenLabel
    ), this.selectedCountry);

    switch (status) {
    case "user-added":
      return {
        status,
        iconClass: "fa fa-check",
        accentIconClass: "fa fa-question",
        badgeClass: "allergen-badge-user",
        tooltip: this.t("allergens_ai_tooltip_user_added", `${displayLabel}: manually added as an allergen.`, displayLabel),
        isPresent: true,
      };
    case "user-confirmed":
      return {
        status,
        iconClass: "fa fa-check",
        badgeClass: "allergen-badge-user",
        tooltip: this.t("allergens_ai_tooltip_ai_detected", `${displayLabel}: AI detected this allergen.`, displayLabel),
        isPresent: true,
      };
    case "user-removed":
      return {
        status,
        iconClass: "fa fa-times",
        badgeClass: "allergen-badge-removed",
        tooltip: this.t("allergens_ai_tooltip_user_removed", `${displayLabel}: manually marked as not an allergen.`, displayLabel),
        isPresent: false,
      };
    case "ai":
    default:
      return {
        status: "ai",
        iconClass: "fa fa-check",
        badgeClass: "allergen-badge-ai",
        tooltip: this.t("allergens_ai_tooltip_detected_by_ai", `${displayLabel}: detected by AI as an allergen.`, displayLabel),
        isPresent: true,
      };
    }
  }

    getAllergenColumnTooltip(column: MatrixColumn) {
      const symptomNote = this.allergenSymptomNotesLookup[column.key]
        || this.t("allergens_ai_tooltip_fallback_symptoms", "Review ingredient evidence and product specifications for symptoms and allergen risk.");
      const scope = column.isOptionalAllergen
        ? this.t("allergens_ai_tooltip_optional_scope", "Optional allergen. This is visible in the allergen catalog but is not part of the selected country rule.")
        : this.t("allergens_ai_tooltip_country_scope", "Country-rule allergen.");
      return this.t(
        "allergens_ai_column_tooltip",
        `${column.label}. ${scope} Common reaction notes: ${symptomNote}`,
        column.label,
        scope,
        symptomNote
      );
    }

  getOverrideIconClass(row: MatrixRow, column: MatrixColumn) {
    const state = row.cellStates[column.key];

    if (state?.status === "user-removed") {
      return "fa fa-times";
    }

    return row.flagged.indexOf(column.key) !== -1 ? "fa fa-check" : "fa fa-plus";
  }

  getOverrideBadgeClass(row: MatrixRow, column: MatrixColumn) {
    const state = row.cellStates[column.key];

    if (state?.status === "user-removed") {
      return "allergen-badge-removed";
    }

    if (state?.status === "user-added") {
      return "allergen-badge-user";
    }

    if (state?.status === "ai") {
      return "allergen-badge-ai";
    }

    return row.flagged.indexOf(column.key) !== -1 ? "allergen-badge-ai" : column.colorClass;
  }

  getOverrideCellTooltip(row: MatrixRow, column: MatrixColumn) {
    const state = row.cellStates[column.key];

    if (state) {
      return state.tooltip;
    }

    if (row.flagged.indexOf(column.key) !== -1) {
      return this.t("allergens_ai_override_tooltip_disable", `Disable ${column.label}`, column.label);
    }

    return this.t("allergens_ai_override_tooltip_mark_as", `Mark as ${column.label}`, column.label);
  }

  startProgressSimulation() {
    this.stopProgressSimulation();
    this.analysisProgress = 0;

    this.progressTimer = window.setInterval(() => {
      if (this.analysisProgress >= 94) {
        this.stopProgressSimulation();
        return;
      }

      const increment = this.analysisProgress < 30
        ? 9
        : this.analysisProgress < 60
          ? 6
          : this.analysisProgress < 84
            ? 4
            : 2;

      this.analysisProgress = Math.min(94, this.analysisProgress + increment);
    }, 160);
  }

  stopProgressSimulation() {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }

  async completeProgressSimulation(runId: number) {
    this.stopProgressSimulation();

    if (runId !== this.analysisRunId) {
      return;
    }

    this.analysisProgress = 100;
    await this.wait(1500);
  }

  wait(ms: number) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  buildExportFilename() {
    const safeName = (this.selectedItem?.name || "allergens-ai")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "") || "allergens-ai";

    return `${safeName}-allergens-ai.pdf`;
  }

  formatAllergenName(value: string) {
    if (this.selectedCountry === NutrientSource.UK && isCerealsContainingGlutenName(value)) {
      return "Cereals";
    }

    return formatAllergenName(value);
  }

  allergenChipClass(value: string) {
    const normalizedKey = resolvePresentationAllergenKey(value || "", value || "");
    const column = this.matrixColumns.find((item) => item.key === normalizedKey);

    return column?.colorClass || "";
  }

  buildAllergenPdfMarkup() {
    if (!this.selectedItem || !this.analysisResult) {
      return "";
    }

    const exportRoot = this.$refs.analysisExportRoot as HTMLElement | undefined;

    if (!exportRoot) {
      return "";
    }

    const reportDate = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date());
    const styles = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
      .map((node) => node.outerHTML)
      .join("\n");
    const clonedContent = exportRoot.cloneNode(true) as HTMLElement;

    clonedContent.querySelectorAll([
      "[data-html2pdf-ignore]",
      "button",
      ".allergens-matrix-arrow",
      ".allergens-overview-help-btn",
      ".allergens-overview-override-btn",
      ".allergens-primary-action",
    ].join(", ")).forEach((node) => node.remove());
    clonedContent.querySelectorAll("select").forEach((select) => {
      const staticValue = document.createElement("span");

      staticValue.className = "form-control allergens-export-static-control";
      staticValue.textContent = this.selectedScopeLabel;
      select.replaceWith(staticValue);
    });

    return `
      <!doctype html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=1440, initial-scale=1" />
          <title>${this.t("allergens_ai_report_title", "AI Allergen Detector Report")}</title>
          ${styles}
          <style>
            html,
            body {
              width: 100%;
              min-width: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            body {
              margin: 0;
              padding: 0;
              background: #f4f2ef;
              overflow-x: hidden;
            }
            .print-shell-fit {
              width: 720px;
              max-width: 720px;
              margin: 0 auto;
              overflow: hidden;
            }
            .print-shell {
              width: 720px;
              max-width: 720px;
              margin: 0 auto;
              padding: 0;
            }
            .print-shell *,
            .print-shell *::before,
            .print-shell *::after {
              box-sizing: border-box;
            }
            .print-shell .allergens-workspace-toolbar,
            .print-shell .allergens-summary-cards,
            .print-shell .allergens-evidence-panel,
            .print-shell .allergens-workspace-disclaimer,
            .print-legend {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .print-shell .allergens-overview-card,
            .print-shell .allergens-matrix-shell,
            .print-shell .allergens-matrix-scroll,
            .print-shell .allergens-matrix-table {
              page-break-inside: auto !important;
              break-inside: auto !important;
            }
            .print-shell .allergens-matrix-table thead {
              display: table-header-group !important;
            }
            .print-shell .allergens-matrix-table tbody {
              display: table-row-group !important;
            }
            .print-shell .allergens-matrix-table tr,
            .print-shell .allergens-matrix-table tbody tr,
            .print-shell .allergens-matrix-table tbody td {
              page-break-inside: avoid !important;
              break-inside: avoid-page !important;
            }
            .print-shell .allergens-matrix-scroll,
            .print-shell .table-responsive {
              width: 100% !important;
              max-width: 100% !important;
              overflow: hidden !important;
            }
            .print-shell .allergens-matrix-table {
              width: 100% !important;
              min-width: 0 !important;
              table-layout: fixed !important;
            }
            .print-shell .allergens-matrix-table th,
            .print-shell .allergens-matrix-table td {
              min-width: 0 !important;
            }
            .print-shell .allergens-matrix-table .allergens-ingredient-column {
              width: 22% !important;
              min-width: 0 !important;
              position: static !important;
            }
            .print-shell .allergens-matrix-table .allergens-column-heading,
            .print-shell .allergens-matrix-table .allergens-flag-column {
              width: auto !important;
              min-width: 0 !important;
            }
            .print-shell .allergens-column-heading-icon {
              width: 36px !important;
              height: 36px !important;
              font-size: 16px !important;
            }
            .print-shell .allergens-column-heading-label {
              font-size: 10px !important;
            }
            .print-shell .allergen-badge {
              width: 28px !important;
              height: 28px !important;
              font-size: 12px !important;
            }
            .print-shell .allergens-bottom-section,
            .print-shell .allergens-bottom-section > div,
            .print-shell .allergens-evidence-panel,
            .print-shell .allergens-workspace-disclaimer,
            .print-legend {
              clear: both !important;
              float: none !important;
              display: block !important;
              width: 100% !important;
              max-width: 100% !important;
            }
            .print-shell .allergens-bottom-section {
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            .print-shell .allergens-bottom-section > div {
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            .print-shell .allergens-evidence-panel {
              min-height: 0 !important;
              height: auto !important;
              overflow: visible !important;
            }
            .print-shell .allergens-workspace-disclaimer {
              position: relative !important;
              z-index: 1;
              margin-top: 20px !important;
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 16px;
              margin-bottom: 22px;
              padding: 0 0 16px;
              border-bottom: 1px solid #e5e7eb;
            }
            .print-header h1 {
              margin: 0 0 4px;
              color: #1f2937;
              font-size: 28px;
              font-weight: 700;
              line-height: 1.2;
            }
            .print-header p {
              margin: 0;
              color: #6b7280;
              font-size: 13px;
              line-height: 1.45;
            }
            .print-header-kicker {
              margin: 0 0 4px;
              color: #8a6427;
              font-size: 11px;
              font-weight: 700;
              letter-spacing: 0.14em;
              line-height: 1.2;
              text-transform: uppercase;
            }
            .print-header-logo {
              flex: 0 0 auto;
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
            }
            .print-header-logo img {
              display: block;
              width: 92px;
              height: auto;
              object-fit: contain;
            }
            .print-shell .allergens-toolbar-actions {
              align-items: flex-end;
            }
            .print-shell .allergens-overview-header {
              align-items: center;
            }
            .print-shell .allergens-overview-header-actions {
              justify-content: flex-end;
            }
            .allergens-export-static-control {
              display: inline-flex !important;
              align-items: center;
              min-height: 31px;
              padding: 6px 12px;
              border: 1px solid #d6d9de;
              border-radius: 0;
              background: #ffffff;
              color: #4b5563;
              font-size: 12px;
              line-height: 1.4;
            }
            .print-legend {
              margin-top: 18px;
              padding: 16px;
              border: 1px solid #d6e0ea;
              border-radius: 14px;
              background: #ffffff;
            }
            .print-legend h2 {
              margin: 0 0 12px;
              color: #1f2937;
              font-size: 18px;
            }
            .print-legend-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 10px 18px;
            }
            .print-legend-row {
              display: flex;
              align-items: center;
              gap: 10px;
              color: #475569;
              font-size: 12px;
              line-height: 1.4;
            }
            .print-legend-badge {
              width: 24px;
              height: 24px;
              border-radius: 50%;
              position: relative;
              display: inline-grid;
              flex: 0 0 auto;
              place-items: center;
              color: #ffffff;
              font-size: 11px;
              font-family: Arial, sans-serif;
              font-weight: 700;
            }
            .print-legend-badge.ai {
              background: #2f80c8;
            }
            .print-legend-badge.user {
              background: #1f9d55;
            }
            .print-legend-badge.removed {
              background: #d92d20;
            }
            .print-legend-badge.empty {
              background: #ffffff;
              color: #94a3b8;
              border: 1px dashed #cbd5e1;
            }
            .print-shell .allergen-badge-accent {
              display: none !important;
            }
            .print-legend-accent {
              position: absolute;
              top: -4px;
              right: -5px;
              width: 13px;
              height: 13px;
              border-radius: 50%;
              display: inline-grid;
              place-items: center;
              background: #f7fbff;
              color: #1f9d55;
              border: 1px solid #cfe1f0;
              font-size: 7px;
              font-family: Arial, sans-serif;
              font-weight: 700;
            }
            .print-warnings {
              margin: 0;
              padding-left: 18px;
              color: #475569;
              font-size: 13px;
              line-height: 1.6;
            }
            @page {
              size: letter portrait;
              margin: 10mm;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <main class="print-shell-fit">
            <div class="print-shell">
              <header class="print-header">
                <div>
                  <div class="print-header-kicker">${this.t("allergens_ai_report_title", "AI Allergen Detector Report")}</div>
                  <h1>${this.selectedItem.name}</h1>
                  <p>${this.selectedTypeLabel} | ${this.selectedScopeLabel}</p>
                  <p>${reportDate}</p>
                </div>
                <div class="print-header-logo">
                  <img src="${menuSanoLogo}" alt="MenuSano Logo" />
                </div>
              </header>

              ${clonedContent.innerHTML}

              <section class="print-legend">
                <h2>${this.t("allergens_ai_legend_title", "Legend")}</h2>
                <div class="print-legend-grid">
                  <div class="print-legend-row">
                    <span class="print-legend-badge ai">✓</span>
                    <span>${this.t("allergens_ai_legend_ai", "AI detected this allergen.")}</span>
                  </div>
                  <div class="print-legend-row">
                    <span class="print-legend-badge user">✓</span>
                    <span>${this.t("allergens_ai_legend_user_added", "Manually added as an allergen.")}</span>
                  </div>
                  <div class="print-legend-row">
                    <span class="print-legend-badge removed">×</span>
                    <span>${this.t("allergens_ai_legend_removed", "Manually marked as not an allergen.")}</span>
                  </div>
                  <div class="print-legend-row">
                    <span class="print-legend-badge empty"></span>
                    <span>${this.t("allergens_ai_legend_empty", "No reportable allergen is present for this ingredient and country.")}</span>
                  </div>
                </div>
              </section>

              <section class="print-warnings">
                <h2>Disclaimer</h2>
                <p>${this.t("allergens_ai_disclaimer", "AI Allergen Detector helps identify likely allergens and prepare label guidance. Please review the ingredients and final label content before publishing or printing.")}</p>
              </section>
            </div>
          </main>
        </body>
      </html>
    `;
  }

  buildExportContainer(markup: string) {
    const parser = new DOMParser();
    const exportDocument = parser.parseFromString(markup, "text/html");
    const container = document.createElement("div");

    container.style.position = "fixed";
    container.style.left = "-10000px";
    container.style.top = "0";
    container.style.width = "720px";
    container.style.background = "#ffffff";
    container.style.zIndex = "-1";

    Array.from(exportDocument.head.children).forEach((node) => {
      container.appendChild(node.cloneNode(true));
    });

    Array.from(exportDocument.body.children).forEach((node) => {
      container.appendChild(node.cloneNode(true));
    });

    return container;
  }

  loadHtml2Pdf() {
    const existingHtml2Pdf = (window as any).html2pdf;

    if (existingHtml2Pdf) {
      return Promise.resolve(existingHtml2Pdf);
    }

    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        'script[data-html2pdf-script="true"]',
      ) as HTMLScriptElement | null;

      if (existingScript) {
        existingScript.addEventListener("load", () => resolve((window as any).html2pdf), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("Failed to load html2pdf")), { once: true });
        return;
      }

      const script = document.createElement("script");

      script.src = `${process.env.BASE_URL || "/"}vendor/html2pdf.bundle.min.js`;
      script.async = true;
      script.dataset.html2pdfScript = "true";
      script.onload = () => resolve((window as any).html2pdf);
      script.onerror = () => reject(new Error("Failed to load html2pdf"));
      document.head.appendChild(script);
    });
  }

  async exportCurrentAnalysis() {
    if (!this.selectedItem || !this.analysisResult || this.exportingPdf) {
      return;
    }

    const exportMarkup = this.buildAllergenPdfMarkup();

    if (!exportMarkup) {
      return;
    }

    const exportContainer = this.buildExportContainer(exportMarkup);

    this.exportingPdf = true;
    try {
      const html2pdf = await this.loadHtml2Pdf() as any;

      document.body.appendChild(exportContainer);
      const exportTarget = exportContainer.querySelector(".print-shell-fit") as HTMLElement | null;

      if (!exportTarget) {
        throw new Error("Unable to prepare export target");
      }

      await html2pdf()
        .set({
          margin: [8, 8, 8, 8],
          filename: this.buildExportFilename(),
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
          },
          jsPDF: {
            unit: "mm",
            format: "letter",
            orientation: "portrait",
          },
          pagebreak: {
            mode: ["css", "legacy"],
          },
        })
        .from(exportTarget)
        .save();
    }
    catch (error) {
      this.$snotify.error(this.t("allergens_ai_analysis_failed", "The Allergen AI analysis could not be completed."));
    }
    finally {
      exportContainer.remove();
      this.exportingPdf = false;
    }
  }

  applyAnalysisResult(result: MlAnalysisResponse, preserveIngredientOrder: boolean) {
    this.updateIngredientOrder(result.ingredients, preserveIngredientOrder);
    this.analysisResult = result;
    this.syncSelectedItemAllergenStatus(result);
    this.refreshKey += 1;
  }

  syncSelectedItemAllergenStatus(result: MlAnalysisResponse) {
    if (!this.selectedItem) {
      return;
    }

    this.selectedItem = {
      ...this.selectedItem,
      hasAllergenAnalysis: true,
      hasAllergen: (result.allergens?.length || 0) > 0,
    };
  }

  updateIngredientOrder(ingredients: MlIngredientAnalysisResult[], preserveExisting: boolean) {
    const nextKeys = ingredients.map((ingredient) => this.ingredientMatrixKey(ingredient));

    if (!preserveExisting || this.ingredientOrderKeys.length === 0) {
      this.ingredientOrderKeys = nextKeys;
      return;
    }

    const nextKeySet = new Set(nextKeys);
    const preservedKeys = this.ingredientOrderKeys.filter((key) => nextKeySet.has(key));
    const appendedKeys = nextKeys.filter((key) => preservedKeys.indexOf(key) === -1);

    this.ingredientOrderKeys = preservedKeys.concat(appendedKeys);
  }

  ingredientMatrixKey(ingredient: MlIngredientAnalysisResult) {
    return `${ingredient.ingredientId || ingredient.ingredientName}-${ingredient.lineagePath}`;
  }

  mergeDuplicateMatrixRows(rows: MatrixRow[]) {
    const mergedRows: MatrixRow[] = [];
    const mergedRowsByKey: Record<string, MatrixRow> = {};

    rows.forEach((row) => {
      const dedupeKey = this.matrixRowDedupeKey(row);
      const existingRow = mergedRowsByKey[dedupeKey];

      if (!existingRow) {
        const copiedRow: MatrixRow = {
          ...row,
          flagged: [...row.flagged],
          overviewFlagged: [...row.overviewFlagged],
          flaggedAllergenCodes: this.copyFlaggedAllergenCodes(row.flaggedAllergenCodes),
          symptomNotesByKey: { ...row.symptomNotesByKey },
          cellStates: { ...row.cellStates },
        };

        mergedRowsByKey[dedupeKey] = copiedRow;
        mergedRows.push(copiedRow);
        return;
      }

      this.mergeUniqueValues(existingRow.flagged, row.flagged);
      this.mergeUniqueValues(existingRow.overviewFlagged, row.overviewFlagged);

      Object.keys(row.flaggedAllergenCodes).forEach((allergenKey) => {
        if (!existingRow.flaggedAllergenCodes[allergenKey]) {
          existingRow.flaggedAllergenCodes[allergenKey] = [];
        }

        this.mergeUniqueValues(existingRow.flaggedAllergenCodes[allergenKey], row.flaggedAllergenCodes[allergenKey]);
      });

      Object.keys(row.symptomNotesByKey).forEach((allergenKey) => {
        if (!existingRow.symptomNotesByKey[allergenKey] && row.symptomNotesByKey[allergenKey]) {
          existingRow.symptomNotesByKey[allergenKey] = row.symptomNotesByKey[allergenKey];
        }
      });

      Object.keys(row.cellStates).forEach((allergenKey) => {
        const currentState = existingRow.cellStates[allergenKey];
        const nextState = row.cellStates[allergenKey];

        if (!currentState || this.matrixCellStatePriority(nextState) > this.matrixCellStatePriority(currentState)) {
          existingRow.cellStates[allergenKey] = nextState;
        }
      });
    });

    return mergedRows;
  }

  matrixRowDedupeKey(row: MatrixRow) {
    return row.ingredientId
      ? `ingredient-${row.ingredientId}`
      : `ingredient-name-${(row.ingredient || "").trim().toLowerCase()}`;
  }

  copyFlaggedAllergenCodes(flaggedAllergenCodes: Record<string, string[]>) {
    return Object.keys(flaggedAllergenCodes).reduce((copied, allergenKey) => {
      copied[allergenKey] = [...flaggedAllergenCodes[allergenKey]];
      return copied;
    }, {} as Record<string, string[]>);
  }

  mergeUniqueValues(target: string[], values: string[]) {
    values.forEach((value) => {
      if (target.indexOf(value) === -1) {
        target.push(value);
      }
    });
  }

  matrixCellStatePriority(state: MatrixCellState) {
    switch (state.status) {
      case "user-removed":
        return 4;
      case "user-added":
        return 2;
      default:
        return 1;
    }
  }

  ingredientOrderIndex(ingredient: MlIngredientAnalysisResult) {
    const index = this.ingredientOrderKeys.indexOf(this.ingredientMatrixKey(ingredient));
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  }

  getMatrixScroller() {
    return this.$refs.matrixScroller as HTMLElement | undefined;
  }

  getOverrideGridScroller() {
    return this.$refs.overrideGridScroller as HTMLElement | undefined;
  }

  updateAllergenScrollCues() {
    this.updateMatrixScrollCues();
    this.updateOverrideGridScrollCues();
    this.optionalBadgeRefreshKey += 1;
  }

  updateMatrixScrollCues() {
    const scroller = this.getMatrixScroller();

    if (!scroller) {
      this.showMatrixScrollLeft = false;
      this.showMatrixScrollRight = false;
      return;
    }

    const maxScrollLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    this.showMatrixScrollLeft = scroller.scrollLeft > 8;
    this.showMatrixScrollRight = maxScrollLeft - scroller.scrollLeft > 8;
  }

  updateOverrideGridScrollCues() {
    const scroller = this.getOverrideGridScroller();

    if (!scroller) {
      this.showOverrideScrollLeft = false;
      this.showOverrideScrollRight = false;
      this.showOverrideScrollUp = false;
      this.showOverrideScrollDown = false;
      return;
    }

    const maxScrollLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    const maxScrollTop = Math.max(scroller.scrollHeight - scroller.clientHeight, 0);
    this.showOverrideScrollLeft = scroller.scrollLeft > 8;
    this.showOverrideScrollRight = maxScrollLeft - scroller.scrollLeft > 8;
    this.showOverrideScrollUp = scroller.scrollTop > 8;
    this.showOverrideScrollDown = maxScrollTop - scroller.scrollTop > 8;
  }

  handleMatrixScroll() {
    this.updateMatrixScrollCues();
    this.optionalBadgeRefreshKey += 1;
  }

  handleOverrideGridScroll() {
    this.updateOverrideGridScrollCues();
    this.optionalBadgeRefreshKey += 1;
  }

  isAdminProcessingWarning(warning: string) {
    const normalizedWarning = (warning || "").toLowerCase();
    return normalizedWarning.indexOf("admin allergen processing is required before this ingredient can be evaluated") !== -1
      || normalizedWarning.indexOf("ingredient allergen data was not found for") !== -1;
  }

  scrollMatrix(direction: "left" | "right") {
    const scroller = this.getMatrixScroller();

    if (!scroller) {
      return;
    }

    const offset = Math.max(scroller.clientWidth * 0.55, 220);
    scroller.scrollBy({
      left: direction === "right" ? offset : -offset,
      behavior: "smooth",
    });

    window.setTimeout(() => this.updateMatrixScrollCues(), 240);
  }

  openOverrideGrid() {
    this.hideAllergenPopovers();
    this.hideOverrideFloatingTooltip();
    this.showOverrideGrid = true;
    this.$nextTick(() => this.updateOverrideGridScrollCues());
  }

  scrollOverrideGrid(direction: "left" | "right" | "up" | "down") {
    const scroller = this.getOverrideGridScroller();

    if (!scroller) {
      return;
    }

    const horizontalOffset = Math.max(scroller.clientWidth * 0.55, 260);
    const verticalOffset = Math.max(scroller.clientHeight * 0.55, 180);

    scroller.scrollBy({
      left: direction === "right" ? horizontalOffset : direction === "left" ? -horizontalOffset : 0,
      top: direction === "down" ? verticalOffset : direction === "up" ? -verticalOffset : 0,
      behavior: "smooth",
    });

    window.setTimeout(() => this.updateOverrideGridScrollCues(), 240);
  }

  handleOpenStats(payload: { item: WellnessItem; tab: WellnessTab }) {
    this.auditLogItem = {
      id: Number(payload.item.id),
      name: payload.item.name,
      createdOn: payload.item.createdOn || null,
      updatedOn: payload.item.updatedOn || null,
      createdUserName: payload.item.createdUserName || "",
      updatedUserName: payload.item.updatedUserName || "",
      requiresDensityUpdate: payload.item.requiresDensityUpdate || null,
    };

    if (payload.tab === "recipes") {
      this.auditModalTitle = this.translations.get("recipe_stats");
    }
    else if (payload.tab === "combinedRecipes") {
      this.auditModalTitle = this.translations.get("dish_stats");
    }
    else {
      this.auditModalTitle = this.translations.get("ingredient_stats");
    }

    this.showAuditLogModal = true;
  }

  async handleOpenUsage(payload: { item: WellnessItem; tab: WellnessTab }) {
    if (payload.tab !== "customRecipes") {
      return;
    }

    try {
      const response = await listRecipesThatUseIngredient(Number(payload.item.id));
      this.ingredientUsageModal.recipes = response.data.result;
      this.ingredientUsageModal.isOpen = true;
    }
    catch (error: any) {
      if (error.response) {
        this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    }
  }
}

export default AllergensAi;
